import * as React from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import { focusVisibleRing, focusVisibleRingError } from './constants';

const inputGroupVariants = cva(
  [
    'group/input-group relative flex w-full items-center rounded-md border border-border bg-background transition-[color,box-shadow] outline-none',
    'has-[>textarea]:h-auto',

    // Variants based on alignment.
    'has-[>[data-align=inline-start]]:[&>input]:pl-2',
    'has-[>[data-align=inline-end]]:[&>input]:pr-2',
    'has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-start]]:[&>input]:pb-3',
    'has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3',

    // Focus and error states - works for both containers and individual inputs
    focusVisibleRing,
    focusVisibleRingError,
  ],
  { defaultVariants: { size: 'md' }, variants: { size: { sm: 'h-9', md: 'h-10' } } }
);

export interface InputGroupProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof inputGroupVariants> {}

const InputGroup = ({
  className,
  size,
  ref,
  ...props
}: InputGroupProps & { ref?: React.Ref<HTMLDivElement> }) => {
  return (
    <div
      ref={ref}
      data-slot="input-group"
      role="group"
      className={cn(inputGroupVariants({ size }), className)}
      {...props}
    />
  );
};

const inputGroupAddonVariants = cva(
  "flex h-auto cursor-text items-center justify-center gap-2 py-1.5 text-sm font-medium text-muted-foreground select-none group-data-[disabled=true]/input-group:opacity-50 [&>kbd]:rounded-[calc(var(--radius)-5px)] [&>svg:not([class*='size-'])]:size-4",
  {
    variants: {
      align: {
        'inline-start': 'order-first pl-3 has-[>button]:ml-[-0.45rem] has-[>kbd]:ml-[-0.35rem]',
        'inline-end': 'order-last pr-3 has-[>button]:mr-[-0.4rem] has-[>kbd]:mr-[-0.35rem]',
        'block-start':
          'order-first w-full justify-start px-3 pt-3 group-has-[>input]/input-group:pt-2.5 [.border-b]:pb-3',
        'block-end':
          'order-last w-full justify-start px-3 pb-3 group-has-[>input]/input-group:pb-2.5 [.border-t]:pt-3',
      },
    },
    defaultVariants: { align: 'inline-start' },
  }
);

export interface InputGroupAddonProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof inputGroupAddonVariants> {}

const InputGroupAddon = ({
  className,
  align = 'inline-start',
  ref,
  ...props
}: InputGroupAddonProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const hasAction = props.onClick || props.onKeyDown;

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    e.currentTarget.parentElement?.querySelector('input')?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if ((e.target as HTMLElement).closest('button')) {
        return;
      }
      e.currentTarget.parentElement?.querySelector('input')?.focus();
    }
  };

  // If the Addon does not have any action, we treat it as a simple div. Otherwise it will still show focus and tab index messing up accessibility
  const actionProps = hasAction
    ? { role: 'button', onClick: handleClick, onKeyDown: handleKeyDown, tabIndex: 0 }
    : {};

  return (
    <div
      {...actionProps}
      ref={ref}
      role="button"
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      {...props}
    />
  );
};

const inputGroupButtonVariants = cva('flex items-center gap-2 text-sm shadow-none', {
  variants: {
    size: {
      xs: "h-6 gap-1 rounded-[calc(var(--radius)-5px)] px-2 has-[>svg]:px-2 [&>svg:not([class*='size-'])]:size-3.5",
      sm: 'h-8 gap-1.5 rounded-md px-2.5 has-[>svg]:px-2.5',
      'icon-xs': 'size-6 rounded-[calc(var(--radius)-5px)] p-0 has-[>svg]:p-0',
      'icon-sm': 'size-8 p-0 has-[>svg]:p-0',
    },
  },
  defaultVariants: { size: 'xs' },
});

export interface InputGroupButtonProps
  extends
    Omit<React.ComponentProps<typeof Button>, 'size'>,
    VariantProps<typeof inputGroupButtonVariants> {}

const InputGroupButton = ({
  className,
  type = 'button',
  variant = 'ghost',
  size = 'xs',
  ref,
  ...props
}: InputGroupButtonProps & { ref?: React.Ref<HTMLButtonElement> }) => {
  return (
    <Button
      ref={ref}
      type={type}
      data-size={size}
      variant={variant}
      className={cn(inputGroupButtonVariants({ size }), className)}
      {...props}
    />
  );
};

export interface InputGroupTextProps extends React.HTMLAttributes<HTMLSpanElement> {}

const InputGroupText = ({
  className,
  ref,
  ...props
}: InputGroupTextProps & { ref?: React.Ref<HTMLSpanElement> }) => {
  return (
    <span
      ref={ref}
      className={cn(
        "flex items-center gap-2 text-sm text-muted-foreground [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    />
  );
};

export interface InputGroupInputProps extends React.ComponentProps<typeof Input> {}

const InputGroupInput = ({
  className,
  ref,
  ...props
}: InputGroupInputProps & { ref?: React.Ref<HTMLInputElement> }) => {
  return (
    <Input
      ref={ref}
      data-slot="input-group-control"
      className={cn(
        'flex-1 rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0',
        className
      )}
      {...props}
    />
  );
};

export interface InputGroupTextareaProps extends React.ComponentProps<typeof Textarea> {}

const InputGroupTextarea = ({
  className,
  ref,
  ...props
}: InputGroupTextareaProps & { ref?: React.Ref<HTMLTextAreaElement> }) => {
  return (
    <Textarea
      ref={ref}
      data-slot="input-group-control"
      className={cn(
        'flex-1 resize-none rounded-none border-0 bg-transparent py-3 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0',
        className
      )}
      {...props}
    />
  );
};

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
  InputGroupInput,
  InputGroupTextarea,
};
